import type { ControlType } from '@risksmart-app/domain/src/types/consts';
import type { ScheduleTRPCInput } from '@risksmart-app/trpc/src/types';
import type {
  UpdateChildControlInput,
  UpdateControlMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useChangeRequestConfirm } from 'src/providers/change-request-confirm-context';
import { useTRPC } from 'src/utils/trpc';

import { withChangeRequestConfirm } from '../_with-change-request-confirm';
import { invalidateChangeRequestFeeds } from '../invalidateChangeRequestFeeds';
import { unwrapApprovalGated } from '../unwrapApprovalGated';

type UpdateControlTRPCInput = Omit<
  UpdateChildControlInput,
  'schedule' | 'Type'
> &
  ScheduleTRPCInput & {
    Type?: ControlType | null;
  };

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
const mapTrpcResponseToGraphQL = (_trpcData: {
  Id: string;
}): UpdateControlMutation => {
  return {
    updateChildControl: {
      __typename: 'GenericMutationOutput',
      affected_rows: 1,
    },
  };
};

export const useUpdateControlTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { requestConfirmation } = useChangeRequestConfirm();
  const mutation = useMutation({
    ...trpc.frontend.control.update.mutationOptions({
      onSuccess: async (result) => {
        // RSP-5789 — when the mutation was gated into a change request
        // (created or amended), surface it on the CR read surfaces
        // immediately instead of waiting for the panel's next poll.
        if (result.status !== 'applied') {
          await invalidateChangeRequestFeeds(queryClient, trpc);
        }
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.control.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.control.controlById.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.control.controlsByUserId.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.control.controlsBasic.queryKey(),
        });
      },
    }),
  });

  return {
    updateControl: async (
      variables: UpdateControlTRPCInput
    ): Promise<UpdateControlMutation> => {
      const result = await withChangeRequestConfirm({
        input: variables,
        run: (v) => mutation.mutateAsync(v),
        requestConfirmation,
      });

      // A confirmed update resolves as `applied` OR `change-request-created` /
      // `change-request-amended` (a CR was raised) — all success, like
      // useDeleteRiskTRPC. expectApplied would throw on the CR branch and
      // surface a false "Failed to save changes" toast. Echo the input Id on the
      // CR branch (the entity is unchanged).
      return mapTrpcResponseToGraphQL(
        unwrapApprovalGated(result, { Id: variables.Id })
      );
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
